import { CardVariant } from "../../models/CardVariant"
import { FormatRulesDictionary, TOURNAMENT_FORMAT_MAX_SERIAL_MAP } from "../../models/TournamentFormat"
import { CardElementName, elementNumberMap } from "../../models/CardElement"
import { Deck, DeckState } from "../../models/Deck"
import { generateOctgnMissingIdReport } from "../../pages/DeckBuilderPage/OctgnXmlGenerator"
import { ErrataSerialDictionary } from "../../models/Errata"
import { getBanReport, getRestrictedReport } from "../CardVariantImage/CardVariantImageBanOverlay"
import { TcgPlayerCardDetailsDictionary } from "../../models/TcgPlayer"
import { addTimedNotification, showFailure } from "../NotificationStack/notificationSlice"
import { PriceDictionary } from "../../models/Price"
import { getRandom } from "../../functions/Common"
import { CARD_TEXT_RULES } from "../../functions/CardRulesEngine"
import { CardFilterCommands } from "../../functions/filter/CardFilterCommands"
import { VariantCache } from "../../appSlice"

export function getDefaultDeckState(id: string): DeckState {
    return {
        editableFields: {
            saved: {
                rank: "",
                profileVariantId: "",
                format: "Standard",
                isPublic: true,
                name: "",
                totalCardsBySerial: {},
                totalCardsByVariant: {},
                elements: [],
                playerName: "",
                notes: null,
                customTags: []
            },
            unsaved: {
                rank: "",
                profileVariantId: "",
                format: "Standard",
                isPublic: true,
                name: "",
                totalCardsBySerial: {},
                totalCardsByVariant: {},
                elements: [],
                playerName: "",
                notes: null,
                customTags: []
            }
        },
        nonEditableFields: {
            isTournamentDeck: false,
            displayName: "",
            createdDate: 0,
            id: id,
            updatedDate: 0,
            userId: "",
            tournamentId: "",
            tournamentName: "",
            views: 0,
            elements: { Dark: 0, Earth: 0, Fire: 0, Ice: 0, Light: 0, Lightning: 0, Water: 0, Wind: 0 },
            needsArchetypeProcessing: false,
            archetypeIds: [],
            archetypes: []
        },
        formatSelection: {
            label: "Standard",
            value: "Standard"
        },
        customTagsSelections: [],
        elementSelection: [],
        isLiked: false,
        totalBackups: 0,
        totalCards: 0,
        totalForwards: 0,
        totalMonsters: 0,
        totalSummons: 0,
        totalLimitBreak: 0,
        backups: {},
        forwards: {},
        monsters: {},
        summons: {},
        limitBreak: {},
        cards: {},
        totalCardsByCost: {},
        totalExBursts: 0,
        totalMultiplayable: 0,
        totalCrystalGenerators: 0,
        totalSpecials: 0,
        totalJobs: {},
        totalCategories: {},
        deckPrice: 0,
        missingPrices: 0,
        missingOctgnIds: [],
        errata: {},
        bannedCards: [],
        restrictedCards: [],
        deckImageVariant: null
    };
}

export const getDeckStateFromDbDeck = (
    deck: Deck,
    deckId: string | undefined,
    variantCache: VariantCache,
    errataDictionary: ErrataSerialDictionary,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    variantPriceDictionary?: PriceDictionary,
    currentUserId?: string) => {
    const newDeckState: DeckState = getDefaultDeckState(deckId ?? "");

    newDeckState.nonEditableFields.createdDate = deck.createdDate ?? 0;
    newDeckState.nonEditableFields.updatedDate = deck.updatedDate ?? 0;
    newDeckState.nonEditableFields.userId = deck.userId ?? "";
    newDeckState.nonEditableFields.displayName = deck.displayName ?? "";
    newDeckState.nonEditableFields.isTournamentDeck = deck.isTournamentDeck;
    newDeckState.nonEditableFields.needsArchetypeProcessing = deck.needsArchetypeProcessing;
    newDeckState.nonEditableFields.archetypeIds = deck.archetypeIds ?? [];
    newDeckState.nonEditableFields.archetypes = deck.archetypes ?? [];

    newDeckState.editableFields.saved.isPublic = deck.isPublic;
    newDeckState.editableFields.unsaved.isPublic = deck.isPublic;

    newDeckState.editableFields.saved.name = deck.name;
    newDeckState.editableFields.unsaved.name = deck.name;

    newDeckState.editableFields.saved.format = deck.format;
    newDeckState.editableFields.unsaved.format = deck.format;

    newDeckState.editableFields.saved.elements = deck.elements;
    newDeckState.editableFields.unsaved.elements = deck.elements;

    newDeckState.editableFields.saved.profileVariantId = deck.profileVariantId || "";
    newDeckState.editableFields.unsaved.profileVariantId = deck.profileVariantId || "";

    newDeckState.editableFields.saved.playerName = deck.playerName;
    newDeckState.editableFields.unsaved.playerName = deck.playerName;

    newDeckState.editableFields.saved.rank = deck.rank;
    newDeckState.editableFields.unsaved.rank = deck.rank;

    newDeckState.nonEditableFields.tournamentName = deck.tournamentName;
    newDeckState.nonEditableFields.tournamentId = deck.tournamentId;

    newDeckState.editableFields.saved.notes = deck.notes ?? null;
    newDeckState.editableFields.unsaved.notes = deck.notes ?? null;

    newDeckState.editableFields.saved.customTags = deck.customTags ?? [];
    newDeckState.editableFields.unsaved.customTags = deck.customTags ?? [];

    newDeckState.nonEditableFields.views = deck.views ?? 0;

    if (currentUserId) {
        newDeckState.isLiked = (deck.likes ? deck.likes : []).includes(currentUserId);
    }

    let deckListImageVariant = variantCache.any.all.byId[deck.profileVariantId];
    if (!deckListImageVariant) {
        const randomVariantId = getRandom(Object.keys(deck.totalCardsByVariant)) ?? "";
        deckListImageVariant = variantCache.any.all.byId[randomVariantId];
        deck.profileVariantId = randomVariantId;
    }

    newDeckState.deckImageVariant = deckListImageVariant;

    newDeckState.formatSelection = {
        label: deck.format,
        value: deck.format
    }

    newDeckState.elementSelection = deck.elements
        ? deck.elements.map((element) => ({
            label: element as CardElementName,
            value: element as CardElementName
        }))
        : [];

    newDeckState.customTagsSelections = (deck.customTags ?? []).map(value => ({ label: value, value: value }));

    const variantIds = Object.keys(deck.totalCardsByVariant);
    for (let variantId of variantIds) {
        const matchingVariant = variantCache.any.all.byId[variantId];

        if (!matchingVariant) {
            // TODO: show warning that some cards could not be found.
            continue;
        }

        const quantity = deck.totalCardsByVariant[variantId];
        for (let i = 0; i < quantity; i++) {
            addCard(newDeckState, matchingVariant, errataDictionary, formatRules, grandfatheredByOpus, variantCache, variantPriceDictionary);
        }
    }

    const missingOctgnIds = generateOctgnMissingIdReport(newDeckState);
    newDeckState.missingOctgnIds = missingOctgnIds;
    newDeckState.editableFields.saved = JSON.parse(JSON.stringify(newDeckState.editableFields.unsaved));

    return newDeckState;
}

export const mergeCards = (
    deckState: DeckState,
    totalCardsByVariant: Record<string, number>,
    errataDictionary: ErrataSerialDictionary,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    variantCache: VariantCache,
    variantPriceDictionary?: PriceDictionary,
) => {
    const newDeckState: DeckState = JSON.parse(JSON.stringify(deckState)) as DeckState;

    const variantIds = Object.keys(totalCardsByVariant);
    for (let variantId of variantIds) {
        const matchingVariant = variantCache.any.all.byId[variantId];

        if (!matchingVariant) {
            // TODO: show warning that some cards could not be found.
            continue;
        }

        const quantity = totalCardsByVariant[variantId];
        for (let i = 0; i < quantity; i++) {
            addCard(newDeckState, matchingVariant, errataDictionary, formatRules, grandfatheredByOpus, variantCache, variantPriceDictionary);
        }
    }

    return newDeckState;
}

export const addCard = (
    newDeckState: DeckState,
    variant: CardVariant,
    errataDictionary: ErrataSerialDictionary,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    variantCache: VariantCache,
    variantPriceDictionary?: PriceDictionary,
    showMessage?: boolean) =>
{
    const serial = variant.serial;
    const format = newDeckState.editableFields.unsaved.format;
    
    if (!newDeckState.editableFields.unsaved.totalCardsBySerial[serial]) {
        newDeckState.editableFields.unsaved.totalCardsBySerial[serial] = 0;
    }

    const maxAllowedOfSerial = TOURNAMENT_FORMAT_MAX_SERIAL_MAP[format];
    if (maxAllowedOfSerial != null && newDeckState.editableFields.unsaved.totalCardsBySerial[serial] >= maxAllowedOfSerial) {
        if (showMessage) {
            showFailure(`${maxAllowedOfSerial}/${maxAllowedOfSerial} already added.`, 3);
        }
        return;
    }

    newDeckState.editableFields.unsaved.totalCardsBySerial[serial] += 1;
    newDeckState.cards[serial] = variant;

    if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
        newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id] = 0;
    }

    newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id] += 1;

    addCost(variant, newDeckState, variantPriceDictionary);

    if (variant.card.isLB) {
        newDeckState.totalLimitBreak += 1;
        newDeckState.limitBreak[variant.id] = variant;
        return;
    }

    newDeckState.totalCards += 1;

    switch (variant.card.type) {
        case "Backup":
            newDeckState.totalBackups += 1;
            newDeckState.backups[variant.id] = variant;
            break;
        case "Forward":
            newDeckState.totalForwards += 1;
            newDeckState.forwards[variant.id] = variant;
            break;
        case "Monster":
            newDeckState.totalMonsters += 1;
            newDeckState.monsters[variant.id] = variant;
            break;
        case "Summon":
            newDeckState.totalSummons += 1;
            newDeckState.summons[variant.id] = variant;
            break;
        default:
            // ERROR
            break;
    }

    if (variant.card.elements) {
        for (let elementName of variant.card.elements) {
            const currentValue = newDeckState.nonEditableFields.elements[elementName] ?? 0;
            newDeckState.nonEditableFields.elements[elementName] = currentValue + 1;
        }
    }

    processElements(newDeckState);

    if (variant.card.jobs) {
        for (let job of variant.card.jobs) {
            const currentValue = newDeckState.totalJobs[job];
            if (!currentValue) {
                newDeckState.totalJobs[job] = 0;
            }

            newDeckState.totalJobs[job] += 1;
        }
    }

    if (variant.card.categories) {
        for (let category of variant.card.categories) {
            const currentValue = newDeckState.totalCategories[category];
            if (!currentValue) {
                newDeckState.totalCategories[category] = 0;
            }

            newDeckState.totalCategories[category] += 1;
        }
    }

    if (variant.card.isExBurst) {
        newDeckState.totalExBursts += 1;
    }

    if (CardFilterCommands.special.isMatch(variant)) {
        newDeckState.totalSpecials += 1;
    }

    if (variant.card.isMultiPlayable) {
        newDeckState.totalMultiplayable += 1;
    }

    if (CARD_TEXT_RULES.isCrystalGenerator(variant)) {
        newDeckState.totalCrystalGenerators += 1;
    }

    const missingOctgnIds = generateOctgnMissingIdReport(newDeckState);
    newDeckState.missingOctgnIds = missingOctgnIds;

    const errata: ErrataSerialDictionary = {};
    Object.entries(errataDictionary).forEach(([serial, value]) => {
        if (newDeckState.editableFields.unsaved.totalCardsBySerial[serial]) {
            errata[serial] = value;
        }
    });

    newDeckState.errata = errata;
    newDeckState.bannedCards = getBanReport({
        formatRules: formatRules,
        tournamentFormat: newDeckState.editableFields.unsaved.format,
        variants: Object.keys(newDeckState.editableFields.unsaved.totalCardsBySerial).map(x => variantCache.primary.all.bySerial[x]),
        grandfatheredByOpus: grandfatheredByOpus
    });

    newDeckState.restrictedCards = getRestrictedReport({
        formatRules: formatRules,
        tournamentFormat: newDeckState.editableFields.unsaved.format,
        variants: Object.keys(newDeckState.editableFields.unsaved.totalCardsBySerial).map(x => variantCache.primary.all.bySerial[x]),
        grandfatheredByOpus: grandfatheredByOpus
    });

    if (showMessage) {
        addTimedNotification({
            message: `${variant.serial} - ${variant.card.name} added!`,
            type: "success"
        }, 750);
    }
}

export const removeCard = (
    variant: CardVariant,
    deckState: DeckState,
    errataDictionary: ErrataSerialDictionary,
    formatRules: FormatRulesDictionary,
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,
    baseVariantsBySerialDictionary: Record<string, CardVariant>,
    variantsByIdDictionary: Record<string, CardVariant>,
    variantPriceDictionary?: PriceDictionary
): DeckState => {

    const serial = variant.serial;
    const newDeckState = JSON.parse(JSON.stringify(deckState)) as DeckState;

    if (!deckState.editableFields.unsaved.totalCardsBySerial[serial]) {
        // DISPLAY ERROR
        return deckState;
    }

    newDeckState.editableFields.unsaved.totalCardsBySerial[serial] -= 1;
    if (newDeckState.editableFields.unsaved.totalCardsBySerial[serial] <= 0) {
        delete newDeckState.editableFields.unsaved.totalCardsBySerial[serial];
        delete newDeckState.cards[serial];
    }

    newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id] -= 1;
    if (newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id] <= 0) {
        delete newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id];
    }

    removeCost(variant, newDeckState, variantPriceDictionary);

    if (variant.card.isLB) {
        newDeckState.totalLimitBreak -= 1;

        if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
            delete newDeckState.limitBreak[variant.id];
        }

        return newDeckState;
    }

    newDeckState.totalCards -= 1;

    switch (variant.card.type) {
        case "Backup":
            newDeckState.totalBackups -= 1;
            if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
                delete newDeckState.backups[variant.id];
            }
            break;
        case "Forward":
            newDeckState.totalForwards -= 1;
            if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
                delete newDeckState.forwards[variant.id];
            }
            break;
        case "Monster":
            newDeckState.totalMonsters -= 1;
            if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
                delete newDeckState.monsters[variant.id];
            }
            break;
        case "Summon":
            newDeckState.totalSummons -= 1;
            if (!newDeckState.editableFields.unsaved.totalCardsByVariant[variant.id]) {
                delete newDeckState.summons[variant.id];
            }
            break;
        default:
            // ERROR
            break;
    }

    if (variant.card.elements) {
        for (let elementName of variant.card.elements) {
            const currentValue = newDeckState.nonEditableFields.elements[elementName] ?? 0;

            if (!currentValue) {
                continue;
            }

            newDeckState.nonEditableFields.elements[elementName] = currentValue - 1;
        }
    }

    processElements(newDeckState);

    if (variant.card.jobs) {
        for (let job of variant.card.jobs) {
            let currentValue = newDeckState.totalJobs[job];
            if (currentValue) {
                currentValue -= 1;
            }

            if (currentValue) {
                newDeckState.totalJobs[job] = currentValue;
            } else {
                delete newDeckState.totalJobs[job];
            }
        }
    }

    if (variant.card.categories) {
        for (let category of variant.card.categories) {
            let currentValue = newDeckState.totalCategories[category];
            if (currentValue) {
                currentValue -= 1;
            }

            if (currentValue) {
                newDeckState.totalCategories[category] = currentValue;
            } else {
                delete newDeckState.totalCategories[category];
            }
        }
    }

    if (variant.card.isExBurst && newDeckState.totalExBursts > 0) {
        newDeckState.totalExBursts -= 1;
    }

    if (CardFilterCommands.special.isMatch(variant) && newDeckState.totalSpecials > 0) {
        newDeckState.totalSpecials -= 1;
    }

    if (variant.card.isMultiPlayable && newDeckState.totalMultiplayable > 0) {
        newDeckState.totalMultiplayable -= 1;
    }

    if (CARD_TEXT_RULES.isCrystalGenerator(variant) && newDeckState.totalCrystalGenerators > 0) {
        newDeckState.totalCrystalGenerators -= 1;
    }

    const missingOctgnIds = generateOctgnMissingIdReport(newDeckState);
    newDeckState.missingOctgnIds = missingOctgnIds;

    const errata: ErrataSerialDictionary = {};
    Object.entries(errataDictionary).forEach(([serial, value]) => {
        if (newDeckState.editableFields.unsaved.totalCardsBySerial[serial]) {
            errata[serial] = value;
        }
    });

    newDeckState.errata = errata;
    newDeckState.bannedCards = getBanReport({
        formatRules: formatRules,
        tournamentFormat: newDeckState.editableFields.unsaved.format,
        variants: Object.keys(newDeckState.editableFields.unsaved.totalCardsBySerial).map(x => baseVariantsBySerialDictionary[x]),
        grandfatheredByOpus: grandfatheredByOpus
    });

    newDeckState.restrictedCards = getRestrictedReport({
        formatRules: formatRules,
        tournamentFormat: newDeckState.editableFields.unsaved.format,
        variants: Object.keys(newDeckState.editableFields.unsaved.totalCardsBySerial).map(x => baseVariantsBySerialDictionary[x]),
        grandfatheredByOpus: grandfatheredByOpus
    });

    if (newDeckState.editableFields.unsaved.profileVariantId) {
        const match = newDeckState.editableFields.unsaved.totalCardsByVariant[newDeckState.editableFields.unsaved.profileVariantId];

        if (!match) {
            const randomVariantId = getRandom(Object.keys(newDeckState.editableFields.unsaved.totalCardsByVariant)) ?? "";

            newDeckState.editableFields.unsaved.profileVariantId = randomVariantId;

            newDeckState.deckImageVariant = randomVariantId
                ? variantsByIdDictionary[randomVariantId]
                : null;
        }
    }

    return newDeckState;
}

function removeCost(variant: CardVariant, newDeckState: DeckState, variantPriceDictionary: PriceDictionary | undefined) {
    const cost = variant.card.cost ?? 0;
    const currentTotalCardsOfThisCost = newDeckState.totalCardsByCost[cost] ?? 0;
    newDeckState.totalCardsByCost[cost] = currentTotalCardsOfThisCost - 1;

    if (newDeckState.totalCardsByCost[cost] < 1) {
        delete newDeckState.totalCardsByCost[cost];
    }

    if (variantPriceDictionary) {
        const variantPrice = variantPriceDictionary[variant.id];
        if (variantPrice) {
            newDeckState.deckPrice -= variantPrice.price;
        } else {
            newDeckState.missingPrices -= 1;
        }
    }
}

function addCost(variant: CardVariant, newDeckState: DeckState, variantPriceDictionary: PriceDictionary | undefined) {
    const cost = variant.card.cost ?? 0;
    const currentTotalCardsOfThisCost = newDeckState.totalCardsByCost[cost] ?? 0;
    newDeckState.totalCardsByCost[cost] = currentTotalCardsOfThisCost + 1;

    if (variantPriceDictionary) {
        const variantPrice = variantPriceDictionary[variant.id];
        if (variantPrice) {
            newDeckState.deckPrice += variantPrice.price;
        } else {
            newDeckState.missingPrices += 1;
        }
    }
}

export function generateTcgPlayerMassEntryLink(deck: DeckState, variants: Record<string, CardVariant>, variantsBySerial: Record<string, CardVariant[]>, tcgPlayerCardDetailsDictionary: TcgPlayerCardDetailsDictionary, collection?: Record<string, number>) {
    const useCollection = collection !== undefined;

    if (!deck?.editableFields?.unsaved?.totalCardsByVariant) {
        return;
    }

    let queryParameters = "";
    Object.entries(deck.editableFields.unsaved.totalCardsByVariant).forEach(([variantId, quantity]) => {
        const matchingVariant = variants[variantId];

        if (!matchingVariant || !matchingVariant.tcgPlayerId) {
            return;
        }

        const matchingDetail = tcgPlayerCardDetailsDictionary[matchingVariant.tcgPlayerId];
        if (!matchingDetail) {
            return;
        }

        const alternateVariants = variantsBySerial[matchingVariant.serial];
        let newQuantity = quantity ?? 0;

        if (useCollection && alternateVariants) {
            for (const alternateVariant of alternateVariants) {
                const ownedQuantity = collection[alternateVariant.id] ?? 0;
                newQuantity = newQuantity - ownedQuantity;
            }
        }

        if (!newQuantity || newQuantity <= 0) {
            return;
        }

        if (queryParameters) {
            queryParameters = `${queryParameters}||${newQuantity} ${encodeURIComponent(matchingDetail.name)} [${matchingDetail.setCode}] ${matchingDetail.serial}`;
        } else {
            queryParameters = `${newQuantity} ${encodeURIComponent(matchingDetail.name)} [${matchingDetail.setCode}] ${matchingDetail.serial}`;
        }
    });

    const url = `https://www.tcgplayer.com/massentry?c=${queryParameters}&productline=Final Fantasy TCG`;
    const affiliateUrl = `https://partner.tcgplayer.com/3JJrgM?u=${encodeURIComponent(url)}`;
    return affiliateUrl;
}

function processElements(newDeckState: DeckState) {
    newDeckState.editableFields.unsaved.elements = [];
    Object.entries(newDeckState.nonEditableFields.elements).forEach(([element, qty]) => {
        if (qty < 1) {
            return;
        }

        newDeckState.editableFields.unsaved.elements.push(element as CardElementName);
    });

    newDeckState.editableFields.unsaved.elements.sort((a, b) => {
        return elementNumberMap[a] - elementNumberMap[b];
    });
}